/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import { defineMessages } from 'react-intl'

export const messages = defineMessages({
  cancelButton: {
    id: 'organization.security.role-mapping.cancel-button',
    defaultMessage: 'Cancel',
  },
  saveButton: {
    id: 'organization.security.role-mapping.save-button',
    defaultMessage: 'Save',
  },
  editRoleSuccessfully: {
    id: 'organization.security.role-mapping.edit-role-successfully',
    defaultMessage: 'Role mapping updated',
  },
  createRoleSuccessfully: {
    id: 'organization.security.role-mapping.create-role-successfully',
    defaultMessage: 'Role mapping created',
  },
  errorSavingRoleMapping: {
    id: 'organization.security.role-mapping.error-saving-role-mapping',
    defaultMessage: 'Failed to save the role mapping due to an unknown error. Try again later.',
  },
  roleMappingDetailsTitle: {
    id: 'organization.security.role-mapping.role-mapping-details-title',
    defaultMessage: 'Role mapping details',
  },
  mappingRuleTitle: {
    id: 'organization.security.role-mapping.mapping-rule-title',
    defaultMessage: 'Mapping rule',
  },
  roleMappingVerificationTitle: {
    id: 'organization.security.role-mapping.role-mapping-verification-title',
    defaultMessage: 'Role mapping verification',
  },
  roleMappingVerificationIsRequired: {
    id: 'organization.security.role-mapping.role-mapping-verification-is-required',
    defaultMessage:
      'This mapping impacts the Organization owner role. You must run the role mapping verification before the mapping can be saved.',
  },
})

export const saveErrors = defineMessages({
  'org.role_mapping_rule.syntax_error': {
    id: 'organization.security.role-mapping.save-errors.syntax-error',
    defaultMessage:
      'Invalid rule syntax. You can only use letters numbers, underscores, hyphens, periods, spaces, and asterisks.',
  },
  'org.role_mapping_rule.empty_rule': {
    id: 'organization.security.role-mapping.save-errors.empty-rule',
    defaultMessage: 'The rule cannot be empty.',
  },
  'org.role_mapping_rule.too_many_rules': {
    id: 'organization.security.role-mapping.save-errors.too-many-rules',
    defaultMessage: 'The role mapping has too many rules. The maximum number of rules is 100.',
  },
  'org.role_mapping_rule.unknown_error': {
    id: 'organization.security.role-mapping.save-errors.unknown-error',
    defaultMessage: 'An unknown error occurred while validating the role mapping rule.',
  },
  'org.role_mapping_rule.ambiguous_rule': {
    id: 'organization.security.role-mapping.save-errors.ambiguous-rule',
    defaultMessage: 'The role mapping rule cannot have both "all" and "any" defined.',
  },
  'org.role_mapping_rule.invalid_name': {
    id: 'organization.security.role-mapping.save-errors.invalid-name',
    defaultMessage:
      'The role mapping name is invalid. It must not be empty and must contain less than 256 characters.',
  },
  'org.role_mapping_rule.duplicate_name': {
    id: 'organization.security.role-mapping.save-errors.duplicate-name',
    defaultMessage: 'This role mapping name is already used. Specify a different name.',
  },
  'org.role_mapping_rule.missing_owner_role': {
    id: 'organization.security.role-mapping.save-errors.missing-owner-rule',
    defaultMessage:
      'To avoid losing access to your organization, you must specify at least one role mapping rule that assigns the Organization owner role.',
  },
})

export const formMessages = defineMessages({
  nameRequired: {
    id: `organization.security.role-mapping.name-required`,
    defaultMessage: `A name is required.`,
  },
  ruleRequired: {
    id: `organization.security.role-mapping.rule-required`,
    defaultMessage: `At least one rule is required.`,
  },
  roleAssignmentsRequired: {
    id: `organization.security.role-mapping.role-assignments-required`,
    defaultMessage: `At least one role is required.`,
  },
  attributeValueRequired: {
    id: `organization.security.role-mapping.attribute-value-required`,
    defaultMessage: `An attribute value is required.`,
  },
  roleMappingNameExists: {
    id: `organization.security.role-mapping.name-exists`,
    defaultMessage: `A role mapping with this name already exists. Provide a different name.`,
  },
  duplicateGroupValue: {
    id: `organization.security.role-mapping.duplicate-group-value`,
    defaultMessage: `This value is duplicated. Remove duplicates to keep only one occurrence.`,
  },
  ruleSyntax: {
    id: `organization.security.role-mapping.rule-syntax`,
    defaultMessage: `A rule value can only contain letters, numbers, underscores, hyphens, periods, spaces, and asterisks.`,
  },
})
